import { Button } from "@/components/ui/UIElements/Button";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "../../";

const BusinessFilters = ({
  searchTerm,
  onSearchChange,
  onClearSearch,
  onRefresh,
  isLoading,
}) => {
  const { t } = useTranslation("stores");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const searchInputRef = useRef(null);

  const handleSearchChange = (e) => {
    onSearchChange(e.target.value);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  const hasActiveSearch = searchTerm.trim() !== "";

  const handleClearAndRestoreFocus = () => {
    onClearSearch();
    searchInputRef.current?.focus();
  };

  return (
    <div className="border-border bg-background rounded-lg border p-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        {/* Search Input */}
        <div className="min-w-0 flex-1">
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <Icon name="Search" className="text-muted-foreground h-5 w-5" />
            </div>
            <input
              ref={searchInputRef}
              type="text"
              aria-label={t("search.placeholder")}
              placeholder={t("search.placeholder")}
              value={searchTerm}
              onChange={handleSearchChange}
              className="border-border bg-card focus:ring-ring h-10 w-full rounded-lg border px-4 py-2 pr-10 pl-10 text-white placeholder-gray-400 focus:border-transparent focus:ring-2 focus:outline-none"
            />
            {searchTerm && (
              <Button
                variant="ghost"
                onClick={handleClearAndRestoreFocus}
                aria-label={t("search.clearSearch")}
                className="bg-muted hover:bg-muted absolute top-1/2 right-2 flex h-5 w-5 -translate-y-1/2 transform cursor-pointer items-center justify-center rounded-full transition-colors"
                type="button"
              >
                <svg
                  className="text-foreground h-3 w-3 hover:text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </Button>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3">
          {/* Clear Search Button */}
          {hasActiveSearch && (
            <Button
              variant="ghost"
              type="button"
              onClick={handleClearAndRestoreFocus}
              className="border-border bg-card text-foreground hover:bg-secondary focus:ring-ring inline-flex h-10 items-center rounded-lg border px-3 py-2 text-sm leading-4 font-medium shadow-sm focus:ring-2 focus:ring-offset-2 focus:outline-none"
            >
              <svg
                className="mr-1 h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
              {t("filters.clearSearch")}
            </Button>
          )}

          {/* Refresh Button */}
          <Button
            variant="ghost"
            type="button"
            onClick={handleRefresh}
            disabled={isLoading || isRefreshing}
            aria-busy={isRefreshing}
            className="bg-primary hover:bg-primary/90 focus:ring-ring inline-flex h-10 items-center rounded-lg border border-transparent px-3 py-2 text-sm leading-4 font-medium text-white focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          >
            <svg
              className={`mr-1 h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            {isRefreshing ? t("filters.refreshing") : t("filters.refresh")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BusinessFilters;
